import { defineStore } from 'pinia';
import { useAuthStore, useCommunityStore } from '.';
import apiService from '@/services/api.service';
import { Config } from '@/config';

import type {
  Pagination,
  ProjectDto,
  ProjectListItemDto,
  ProjectConsultationListItemDto,
  UserPermissionDto,
  LocalizableString,
  Locales,
} from '@/services/types/api.types';
import { Constants } from '@/constants';
import { useI18n } from 'vue-i18n';
import router from '@/router';
import { FileUploaderFactory } from '@/factories/file-uploader.factory';
import { ProcessorType, type SavingOptions } from '@/services/types/file-uploader.types';

const findProject = (projects: ProjectDto[], projectId: number) => {
  return projects.find((x) => {
    return x.id == projectId;
  });
};

export interface ProjectsState {
  projects: ProjectDto[];
  projectListItems: ProjectListItemDto[];
  pagination: Pagination;
  consultations: {
    consultationListItems: ProjectConsultationListItemDto[];
    pagination: Pagination;
  };
  isLoading: boolean;
  itemLoadingStates: Set<number>;
}

function getVisibilities(): any[] {
  const { t } = useI18n();
  return [
    { label: t('project.visibility.options.public'), value: Constants.Project.Visibility.Public },
    { label: t('project.visibility.options.private'), value: Constants.Project.Visibility.Private },
  ];
}

function getStatuses(): any[] {
  const { t } = useI18n();
  return [
    { label: t('project.status.options.pre-launch'), value: Constants.Project.Status.PreLaunch },
    { label: t('project.status.options.ongoing'), value: Constants.Project.Status.Ongoing },
    { label: t('project.status.options.completed'), value: Constants.Project.Status.Completed },
    { label: t('project.status.options.archived'), value: Constants.Project.Status.Archived },
  ];
}

export const useProjectStore = defineStore({
  id: 'projects',
  state: (): ProjectsState => ({
    projects: [],
    projectListItems: [],
    pagination: {
      page: 1,
      rowsNumber: 0,
      rowsPerPage: Constants.Pagination.DefaultRowsPerPage,
    },
    consultations: {
      consultationListItems: [],
      pagination: {
        page: 1,
        rowsNumber: 0,
        rowsPerPage: Constants.Pagination.DefaultRowsPerPage,
      },
    },
    isLoading: false,
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    isItemLoading: (state) => (id: number) => state.itemLoadingStates.has(id),
    getItem:
      (state) =>
      (id: number): ProjectDto | undefined =>
        state.projects.find((c) => c.id === id),
    getProjectStatuses: (state) => getStatuses,
    getProjectVisibilities: (state) => getVisibilities,
    previewURL: (state) => (projectId: number) => {
      const project = findProject(state.projects, projectId);
      const communityStore = useCommunityStore();
      if (project) {
        const communityUrl = communityStore.previewURL(project.community_id);
        return `${communityUrl}/projects/${project.slug}`;
      }
    },
  },
  actions: {
    async fetchById(id: number) {
      this.itemLoadingStates.add(id);
      try {
        const { data } = await apiService.get<ProjectDto>(`${Config.api.v2}/projects/${id}`);
        const index = this.projects.findIndex((c) => c.id === id);
        if (index !== -1) {
          this.projects[index] = data;
        } else {
          this.projects.push(data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(id);
      }
    },
    async fetchAll(
      page: number = 1,
      rowsPerPage: number = Constants.Pagination.DefaultRowsPerPage,
      q: string | null = null,
    ) {
      this.isLoading = true;
      try {
        const data = { page, pageSize: rowsPerPage, q };
        const response = await apiService.post<ProjectListItemDto[]>(`${Config.api.v2}/projects/:search`, data);
        if (response.success) {
          this.projectListItems = response.data ?? [];
          if (!response.pagination) return;
          this.pagination = {
            page: response.pagination.currentPage,
            rowsNumber: response.pagination.totalCount,
            rowsPerPage: rowsPerPage,
          };
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    create(options: {
      title?: LocalizableString;
      description?: LocalizableString;
      cloneFromId?: number;
      languageEnabled: Locales[];
    }): ProjectDto {
      const authStore = useAuthStore();
      return {
        community_id: authStore.currentCommunity ?? -1,
        slug: '',
        status: Constants.Project.Status.PreLaunch,
        title: options.title ?? { en: '', fr: '' },
        description: options.description ?? { en: '', fr: '' },
        theme_key: null,
        visibility: Constants.Project.Visibility.Public,
        order_num: -1,
        options: { show_private_everyone: false, automatic_publish: false },
        private_email_allowed: { email: [], domain: [] },
        user_permissions: [],
        is_template: 0,
        open_graph_file: null,
        banner_file: null,
        clone_from_id: options.cloneFromId,
        language_enabled: options.languageEnabled,
      };
    },
    async save(project: ProjectDto) {
      try {
        let projectId = project.id;
        if (projectId) {
          await apiService.put<ProjectDto>(`${Config.api.v2}/projects/${project.id}`, project);
        } else {
          const { data } = await apiService.post<ProjectDto>(`${Config.api.v2}/projects`, project);
          projectId = data.id;
          router.push({ name: 'project_details', params: { id: projectId } });
        }
        await this.fetchById(projectId!);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async saveBanner(projectId: number, options: SavingOptions): Promise<boolean> {
      try {
        await FileUploaderFactory.create(options.version).upload({
          image: options.image,
          method: apiService.post,
          processorType: ProcessorType.Multer,
          url: `${Config.api.v2}/projects/${projectId}/banner`,
        });
        await this.fetchById(projectId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async removeBanner(projectId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/projects/${projectId}/banner`);
        await this.fetchById(projectId);
      } catch (error) {
        console.error(error);
        // this.error = error.message;
      }
    },
    async saveOpenGraph(projectId: number, options: SavingOptions): Promise<boolean> {
      try {
        await FileUploaderFactory.create(options.version).upload({
          image: options.image,
          method: apiService.post,
          processorType: ProcessorType.Multer,
          url: `${Config.api.v2}/projects/${projectId}/open-graph`,
        });
        await this.fetchById(projectId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async removeOpenGraph(projectId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/projects/${projectId}/open-graph`);
        await this.fetchById(projectId);
      } catch (error) {
        console.error(error);
        // this.error = error.message;
      }
    },
    async delete(projectId: number, page: number, q: string | null = null) {
      try {
        await apiService.delete(`${Config.api.v2}/projects/${projectId}`);
        await this.fetchAll(page, this.pagination.rowsPerPage, q);
      } catch (error) {
        console.error(error);
      }
    },
    async fetchProjectStats(projectId: number, timePeriod: number) {
      const requestData = { timePeriod };
      try {
        return await apiService.get(`${Config.api.v2}/projects/${projectId}/visitor-metrics`, requestData);
      } catch (error) {
        console.error(error);
        return { data: [], success: false, error: error instanceof Error ? error.message : 'Unknown error' };
      }
    },
    async fetchUserPermissions(projectId: number) {
      try {
        const { data } = await apiService.get<UserPermissionDto[]>(
          `${Config.api.v2}/projects/${projectId}/user-permissions`,
        );
        const project = findProject(this.projects, projectId);
        if (!project) {
          throw new Error('Project not found');
        }
        project.user_permissions = data;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async addUserPermission(projectId: number, email: string, role: number): Promise<boolean> {
      try {
        console.debug('Creating user permission', email);
        const response = await apiService.post(`${Config.api.v2}/projects/${projectId}/user-permissions`, {
          email,
          role,
        });
        await this.fetchUserPermissions(projectId);
        return response.success;
      } catch (error) {
        console.error(error);
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    async deleteUserPermission(up: UserPermissionDto) {
      try {
        await apiService.delete(`${Config.api.v2}/projects/${up.object_id}/user-permissions/${up.id}`);
        await this.fetchUserPermissions(up.object_id);
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async searchConsultations(projectId: number, page: number = 1, rowsPerPage: number = 5, q: string | null = null) {
      this.isLoading = true;
      try {
        const data = { page, pageSize: rowsPerPage, q };
        const response = await apiService.post<ProjectConsultationListItemDto[]>(
          `${Config.api.v2}/projects/${projectId}/:search-consultations`,
          data,
        );
        if (response.success) {
          this.consultations.consultationListItems = response.data ?? [];
          if (!response.pagination) return;
          this.consultations.pagination = {
            page: response.pagination.currentPage,
            rowsNumber: response.pagination.totalCount,
            rowsPerPage: rowsPerPage,
          };
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
  },
});
